import { AccessAllowedEnum } from './access-allowed.enum';
import { AreaItem } from './area-item';
import { PickupPoint } from './pickup-point';
import { PickupPointGroup } from './pickup-point-group';

export class AreaItemPack {
  private _id: number;
  private _name: string;
  private _description: string;
  private _additionalDescription: string;
  private _svgImage: string;
  private _areaItems: AreaItem[];
  private _showOnHomePage: boolean;
  private _eventRouterName: string;
  private _purchaseNearestTicket: boolean;
  private _hideOnWebshop: boolean;
  private _showTimeSlotTitle: boolean;
  private _groupName: string;
  private _groupMerge: boolean;
  private _groupTimeSlotsPageDescription: string;
  private _groupProductsPageDescription: string;
  private _showQuantityAvailableTickets: boolean;
  private _accessIsAllowedOnlyFor = AccessAllowedEnum.All;
  private _skipBookingSlotPage: boolean;
  private _pickupPoints: PickupPoint[];
  private _pickupPointGroups: PickupPointGroup[];
  private _isPurchaseOnlyTodayTicket: boolean;

  constructor(
    id: number,
    name: string,
    description: string,
    additionalDescription: string,
    svgImage: string,
    showOnHomePage: boolean = false,
    areaItems: AreaItem[] = null,
    eventRouterName: string,
    purchaseNearestTicket: boolean = false,
    hideOnWebshop: boolean = false,
    showTimeSlotTitle: boolean = false,
    groupName: string = '',
    groupMerge: boolean = false,
    groupTimeSlotsPageDescription: string = '',
    groupProductsPageDescription: string = '',
    showQuantityAvailableTickets: boolean = false,
    accessIsAllowedOnlyFor?: AccessAllowedEnum,
    skipBookingSlotPage: boolean = false,
    pickupPoints: PickupPoint[] = [],
    pickupPointGroups: PickupPointGroup[] = [],
    isPurchaseOnlyTodayTicket = false,
  ) {
    this._id = id;
    this._name = name;
    this._description = description;
    this._additionalDescription = additionalDescription;
    this._svgImage = svgImage;
    this._areaItems = areaItems;
    this._showOnHomePage = showOnHomePage;
    this._eventRouterName = eventRouterName;
    this._purchaseNearestTicket = purchaseNearestTicket;
    this._hideOnWebshop = hideOnWebshop;
    this._showTimeSlotTitle = showTimeSlotTitle;
    this._groupName = groupName;
    this._groupMerge = groupMerge;
    this._groupTimeSlotsPageDescription = groupTimeSlotsPageDescription;
    this._groupProductsPageDescription = groupProductsPageDescription;
    this._showQuantityAvailableTickets = showQuantityAvailableTickets;
    this._accessIsAllowedOnlyFor = accessIsAllowedOnlyFor;
    this._skipBookingSlotPage = skipBookingSlotPage;
    this._pickupPoints = pickupPoints;
    this._pickupPointGroups = pickupPointGroups;
    this._isPurchaseOnlyTodayTicket = isPurchaseOnlyTodayTicket;
  }

  static createFromAny(object: any): AreaItemPack {
    if (object === null || object === undefined) {
      return null;
    }

    const areaItems = object.areaItems ? object.areaItems.map(x => AreaItem.createFromAny(x)).sort((n1, n2) => n1.sequence - n2.sequence) : [];

    let accessAllowed = AccessAllowedEnum.All;
    if (object.accessIsAllowedOnlyFor) {
      const properName = (object.accessIsAllowedOnlyFor as string).toString();
      accessAllowed = AccessAllowedEnum[properName] || AccessAllowedEnum.All;
    }

    const packPickupPoints = object.pickupPoints ? object.pickupPoints.map(x => PickupPoint.createFromAny(x)) : [];
    const packPickupPointGroups = object.pickupPointGroups ? object.pickupPointGroups.map(x => PickupPointGroup.createFromAny(x)) : [];

    return new AreaItemPack(
      object.id,
      object.name ? object.name : '',
      object.description,
      object.additionalDescription,
      object.svgImage,
      object.showOnHomePage,
      areaItems,
      object.eventRouterName,
      object.purchaseNearestTicket,
      object.hideOnWebshop,
      object.showTimeSlotTitle,
      object.groupName,
      object.groupMerge,
      object.groupTimeSlotsPageDescription,
      object.groupProductsPageDescription,
      object.showQuantityAvailableTickets,
      accessAllowed,
      object.skipBookingSlotPage,
      packPickupPoints,
      packPickupPointGroups,
      object.isPurchaseOnlyTodayTicket,
    );
  }

  get accessIsAllowedOnlyFor(): AccessAllowedEnum {
    return this._accessIsAllowedOnlyFor;
  }

  get id(): number {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get description(): string {
    return this._description;
  }

  get additionalDescription(): string {
    return this._additionalDescription;
  }

  get svgImage(): string {
    return this._svgImage;
  }

  get areaItems(): AreaItem[] {
    return this._areaItems;
  }

  set areaItems(value: AreaItem[]) {
    this._areaItems = value;
  }

  get hasEvents(): boolean {
    if (this.areaItems != null && this.areaItems.find(x => (x.isBirthday || x.isGroup))) {
      return true;
    }
    return false;
  }

  get showOnHomePage(): boolean {
    return this._showOnHomePage;
  }

  get eventRouterName(): string {
    return this._eventRouterName;
  }

  get purchaseNearestTicket(): boolean {
    return this._purchaseNearestTicket;
  }

  get hideOnWebshop(): boolean {
    return this._hideOnWebshop;
  }

  get showTimeSlotTitle(): boolean {
    return this._showTimeSlotTitle;
  }

  get groupName(): string {
    return this._groupName;
  }

  get groupMerge(): boolean {
    return this._groupMerge;
  }

  get groupTimeSlotsPageDescription(): string {
    return this._groupTimeSlotsPageDescription;
  }

  get groupProductsPageDescription(): string {
    return this._groupProductsPageDescription;
  }

  get showQuantityAvailableTickets(): boolean {
    return this._showQuantityAvailableTickets;
  }

  get skipBookingSlotPage(): boolean {
    return this._skipBookingSlotPage;
  }

  get pickupPoints(): PickupPoint[] {
    return this._pickupPoints;
  }

  get pickupPointGroups(): PickupPointGroup[] {
    return this._pickupPointGroups;
  }


  get isPurchaseOnlyTodayTicket(): boolean {
    return this._isPurchaseOnlyTodayTicket;
  }
}

